import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2 } from 'lucide-react';

import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { PunchupAddButton, PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import {
	PunchupActionsCell,
	PunchupActionsHeaderCell,
	PunchupColumnHeader,
	PunchupDataRow,
	PunchupEntityCell,
	PunchupHeaderRow,
	PunchupTableContainer,
	PunchupTableEmptyRow,
	PunchupTableLoadingRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { postToast } from 'features/common/utils/postFlash';

import { useGatesManagement } from './hooks/useGatesManagement';
import { AddGateDialog } from './components/AddGateDialog';
import { ManageScannersDialog } from './components/ManageScannersDialog';
import { Gate } from './types';
import styles from './PunchupGatesManagement.module.css';

// Gate · Scanners · Actions. The actions track is max-content (it holds two buttons, wider than the
// 80px sticky-actions default); the first two columns share free space via minmax(_, fr).
const GRID_COLUMNS = 'minmax(180px, 1.5fr) minmax(240px, 2fr) max-content';

export const PunchupGatesManagement = () => {
	const { t } = useTranslation();
	const { eventId, gatesQuery, createGate, deleteGate, resolveScannerName } = useGatesManagement();
	const gates = gatesQuery.data;

	const [isAdding, setIsAdding] = useState(false);
	const [manageGateId, setManageGateId] = useState<number | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<Gate | null>(null);

	// Derive the managed gate from the live list (don't store the object) so an assign/remove refetch
	// flows straight into the open dialog without a resync effect.
	const manageGate = manageGateId != null ? gates?.find((gate) => gate.ID === manageGateId) ?? null : null;

	useEffect(() => {
		if (gatesQuery.isError) postToast('error', t('gates-load-error', 'Failed to load gates'));
	}, [gatesQuery.isError, t]);

	const renderBody = () => {
		if (gatesQuery.isLoading) return <PunchupTableLoadingRow />;
		if (!gates?.length) {
			return <PunchupTableEmptyRow text={t('gates-empty', 'No gates yet. Add one to start assigning scanners.')} />;
		}
		return gates.map((gate) => (
			<PunchupDataRow key={gate.ID}>
				<PunchupEntityCell>{gate.name}</PunchupEntityCell>
				<td className={styles.scannersCell}>
					{gate.assigned_scanners.length === 0 ? (
						<span className={styles.noScanners}>{t('gates-no-scanners', 'No scanners assigned')}</span>
					) : (
						<div className={styles.scannerChips}>
							{gate.assigned_scanners.map((scanner) => (
								<span className={styles.scannerChip} key={`${gate.ID}-${scanner.scanner_id}`}>
									{resolveScannerName(scanner.scanner_id)}
								</span>
							))}
						</div>
					)}
				</td>
				<PunchupActionsCell onClick={(e) => e.stopPropagation()}>
					<div className={styles.rowActions}>
						<PunchupButton
							type="button"
							variant="outline"
							text={t('gates-manage', 'Manage')}
							onClick={() => setManageGateId(gate.ID)}
						/>
						<PunchupButton
							type="button"
							variant="destructive"
							icon={<Trash2 size={16} />}
							text={t('gates-delete', 'Delete')}
							onClick={() => setDeleteTarget(gate)}
						/>
					</div>
				</PunchupActionsCell>
			</PunchupDataRow>
		));
	};

	return (
		<PunchupTablePage
			pageName="Gates & Scanners"
			header={
				<PunchupTablePageHeader
					title="Gates & Scanners"
					description="Create gates and assign the staff who scan tickets at each one."
					customAction={
						<PunchupAddButton onClick={() => setIsAdding(true)}>{t('gates-add-cta', 'Add gate')}</PunchupAddButton>
					}
					onRefresh={() => gatesQuery.refetch()}
					refreshLabel={t('button-refresh', 'Refresh')}
					isRefreshing={gatesQuery.isFetching}
				/>
			}
			pageFallback={
				gatesQuery.isError && !gates ? (
					<PunchupErrorState
						title={t('gates-load-error-title', "We couldn't load gates")}
						description={t('gates-load-error-description', 'Something went wrong fetching this show’s gates.')}
						onRetry={() => gatesQuery.refetch()}
						retryLabel={t('button-try-again', 'Try again')}
					/>
				) : undefined
			}
			overlays={
				<>
					{isAdding && (
						<AddGateDialog
							isCreating={createGate.isLoading}
							onClose={() => setIsAdding(false)}
							onSave={(name) => createGate.mutate(name, { onSuccess: () => setIsAdding(false) })}
						/>
					)}
					{manageGate && (
						<ManageScannersDialog gate={manageGate} eventId={eventId} onClose={() => setManageGateId(null)} />
					)}
					{deleteTarget &&
						createPortal(
							<PunchupConfirmDialog
								icon={<AlertTriangle size={24} />}
								iconVariant="destructive"
								title={t('gates-delete-title', 'Delete this gate?')}
								text={t(
									'gates-delete-text',
									'This gate and its scanner assignments will be removed. This cannot be undone.'
								)}
								isLoading={deleteGate.isLoading}
								isDestructive
								buttons={[
									{ label: t('button-cancel', 'Cancel'), variant: 'secondary', onClick: () => setDeleteTarget(null) },
									{
										label: t('gates-delete', 'Delete'),
										variant: 'destructive',
										icon: <Trash2 size={16} />,
										onClick: () => deleteGate.mutate(deleteTarget.ID, { onSuccess: () => setDeleteTarget(null) }),
									},
								]}
							/>,
							document.body
						)}
				</>
			}
		>
			<PunchupTableContainer columns={GRID_COLUMNS} isRefetching={gatesQuery.isFetching && !gatesQuery.isLoading}>
				<thead>
					<PunchupHeaderRow>
						<PunchupColumnHeader>{t('gates-col-name', 'Gate')}</PunchupColumnHeader>
						<PunchupColumnHeader>{t('gates-col-scanners', 'Scanners')}</PunchupColumnHeader>
						<PunchupActionsHeaderCell />
					</PunchupHeaderRow>
				</thead>
				<tbody>{renderBody()}</tbody>
			</PunchupTableContainer>
		</PunchupTablePage>
	);
};
